import { useMemo, useState, type CSSProperties } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  qDeliveryOrdersByPurchaseOrder,
  qPurchaseOrdersList,
  type DeliveryOrderRow,
  type PurchaseOrderRow,
} from '@helios/data-access';
import { usePowerSyncQuery } from '../../adapters/powersync';
import { useFormatMoney } from '../../hooks/useFormatMoney';
import { useBrandTokens, type ShellOutletContext } from '../../shell/shared';
import {
  InventoryListToolbar,
  InventoryPageShell,
  InventoryPrimaryAction,
  InventoryRow,
  InventoryRowActions,
  InventoryStatusBadge,
  InventoryTable,
  type InventoryStatusVariant,
} from './_shared';
import { PurchaseOrderDetailsDrawer } from './PurchaseOrderDetailsDrawer';
import { PurchaseOrderPrintMount, type PurchaseOrderPrintRequest } from './PurchaseOrderPrintMount';
import { SendPurchaseOrderEmailModal } from './SendPurchaseOrderEmailModal';

const COLUMNS = [
  { label: 'PO Number', width: '160px' },
  { label: 'Date', width: '110px' },
  { label: 'Supplier', width: '1.2fr' },
  { label: 'Notes', width: '1fr' },
  { label: 'Status', width: '120px' },
  { label: 'Delivery', width: '140px' },
  { label: 'Total', width: '120px', align: 'right' as const },
  { label: 'Actions', width: '110px', align: 'right' as const },
];

function poStatusVariant(status: string | null): InventoryStatusVariant {
  if (status === 'cancelled') return 'cancelled';
  if (status === 'draft') return 'draft';
  return 'completed';
}

export function PurchaseOrdersPage() {
  const { collapsed = false, onToggleSidebar } = useOutletContext<ShellOutletContext>() ?? {
    collapsed: false,
  };
  const navigate = useNavigate();
  const brandTokens = useBrandTokens();

  const { data: rows = [] } = usePowerSyncQuery<PurchaseOrderRow>(qPurchaseOrdersList());

  const [search, setSearch] = useState('');
  const [selectedPoId, setSelectedPoId] = useState<string | null>(null);
  // PRD.239 — current PO awaiting print. The mount fires triggerPrint()
  // once it has finished loading the PO + lines + supplier + branding,
  // then onAfterPrint clears this. Nothing here mutates the PO.
  const [printRequest, setPrintRequest] = useState<PurchaseOrderPrintRequest | null>(null);
  // PRD.241 — Send Email modal target. Setting to a poId opens the modal;
  // the modal loads its own PO data and posts to psendEmail. No mutation.
  const [emailPoId, setEmailPoId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(
      (r) =>
        (r.po_number ?? '').toLowerCase().includes(q) ||
        (r.supplier_name ?? '').toLowerCase().includes(q) ||
        (r.notes ?? '').toLowerCase().includes(q) ||
        (r.status ?? '').toLowerCase().includes(q),
    );
  }, [rows, search]);

  return (
    <InventoryPageShell
      title="Purchase Orders"
      subtitle="Track intended purchases from suppliers — no stock movement created on save"
      collapsed={collapsed}
      onToggleSidebar={onToggleSidebar}
      primaryAction={
        <InventoryPrimaryAction
          label="Create Order"
          onClick={() => navigate('/inventory/purchase-orders/new')}
        />
      }
    >
      <InventoryListToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search PO number, supplier, notes, status..."
      />
      <div style={{ height: 'var(--sp-4)', flexShrink: 0 }} />
      <InventoryTable
        columns={COLUMNS}
        empty={
          filtered.length === 0
            ? {
                show: true,
                title: 'No purchase orders yet.',
                hint: 'Create a PO to track intended stock orders sent to suppliers.',
              }
            : undefined
        }
      >
        {filtered.map((row) => (
          <PoListRow
            key={row.id}
            row={row}
            onOpen={() => setSelectedPoId(row.id)}
            onReceive={() => navigate(`/inventory/purchase-orders/${row.id}/receive`)}
            onPrint={() => setPrintRequest({ purchaseOrderId: row.id })}
            onEmail={() => setEmailPoId(row.id)}
          />
        ))}
      </InventoryTable>
      <PurchaseOrderDetailsDrawer
        open={selectedPoId != null}
        onOpenChange={(o) => {
          if (!o) setSelectedPoId(null);
        }}
        purchaseOrderId={selectedPoId}
        brandTokens={brandTokens}
        onPrint={(poId) => setPrintRequest({ purchaseOrderId: poId })}
      />
      <PurchaseOrderPrintMount request={printRequest} onAfterPrint={() => setPrintRequest(null)} />
      {/* PRD.241 — Page-level Send Email modal so it's reachable from the
          row action. The drawer renders its own modal instance for its
          internal button; both target psendEmail and are read-only. */}
      <SendPurchaseOrderEmailModal
        open={emailPoId != null}
        onOpenChange={(o) => {
          if (!o) setEmailPoId(null);
        }}
        purchaseOrderId={emailPoId}
        portalStyle={brandTokens as CSSProperties}
      />
    </InventoryPageShell>
  );
}

function PrinterIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="6 9 6 2 18 2 18 9" />
      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
      <rect x="6" y="14" width="12" height="8" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <polyline points="3 7 12 13 21 7" />
    </svg>
  );
}

const rowIconButtonStyle: CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  color: 'var(--text-muted)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: 0,
};

function PoListRow({
  row,
  onOpen,
  onReceive,
  onPrint,
  onEmail,
}: {
  row: PurchaseOrderRow;
  onOpen: () => void;
  onReceive: () => void;
  onPrint: () => void;
  onEmail: () => void;
}) {
  const { data: deliveries = [] } = usePowerSyncQuery<DeliveryOrderRow>(
    qDeliveryOrdersByPurchaseOrder(row.id),
  );
  const fmt = useFormatMoney();
  const hasReceived = deliveries.some((d) => d.status === 'received');
  const hasDraft = deliveries.some((d) => d.status === 'draft');
  const deliveryLabel = hasReceived ? 'Received' : hasDraft ? 'Draft DO' : 'Not Created';

  return (
    <InventoryRow columns={COLUMNS} clickable onClick={onOpen}>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 'var(--font-weight-semi)',
          color: 'var(--text)',
        }}
      >
        {row.po_number ?? row.id.slice(0, 8)}
      </span>
      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
        {row.po_date ?? '—'}
      </span>
      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text)' }}>
        {row.supplier_name ?? '—'}
      </span>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text-muted)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {row.notes ?? '—'}
      </span>
      <InventoryStatusBadge status={poStatusVariant(row.status)} />
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          color: hasReceived ? 'var(--color-success-text)' : 'var(--text-muted)',
          fontWeight: 'var(--font-weight-medium)',
        }}
      >
        {deliveryLabel}
      </span>
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text)',
          textAlign: 'right',
          fontWeight: 'var(--font-weight-medium)',
        }}
      >
        {row.total_amount == null ? '—' : fmt(row.total_amount)}
      </span>
      <div style={{ display: 'flex', gap: 'var(--sp-1)', justifyContent: 'flex-end' }}>
        {/* PRD.239 — Print PO. Available on all saved POs (including
            cancelled), since a printable record is useful for audit. */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onPrint();
          }}
          title="Print Purchase Order"
          aria-label="Print Purchase Order"
          style={rowIconButtonStyle}
        >
          <PrinterIcon />
        </button>
        {/* PRD.241 — Send PO via email. Opens the review modal at the
            page level; modal posts to psendEmail. No DB writes. */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEmail();
          }}
          title="Send Purchase Order via email"
          aria-label="Send Purchase Order via email"
          style={rowIconButtonStyle}
        >
          <MailIcon />
        </button>
        {!hasReceived && row.status !== 'cancelled' && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onReceive();
            }}
            style={{
              height: 28,
              padding: '0 8px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--color-primary)',
              background: 'var(--bg-surface)',
              color: 'var(--color-primary)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Receive
          </button>
        )}
        <InventoryRowActions />
      </div>
    </InventoryRow>
  );
}
